/**
 * Caption Demo Showcase
 * 
 * Demonstrates the advanced caption system with:
 * - Different text animations (14 types)
 * - Various outline effects (6 types) 
 * - Multiple background styles
 * - Unique Google Fonts with large text
 * - Professional color combinations
 * 
 * This demo creates a series of caption items that showcase
 * all the capabilities of the AdvancedTextEngine.
 */

import React from 'react';
import { CaptionItemImpl } from '../types/caption';
import { Position } from '../Utils';

/**
 * Generate tokens with even timing distribution
 */
function generateTokensForDemo(demo) {
    const words = demo.text.split(' ');
    const startMs = demo.startAt * 1000;
    const durationMs = demo.duration * 1000;
    const wordDuration = durationMs / words.length;
    
    const tokens = words.map((word, index) => ({
        text: word,
        fromMs: startMs + (index * wordDuration),
        toMs: startMs + ((index + 1) * wordDuration),
        index: index
    }));
    
    return new CaptionItemImpl({
        ...demo,
        tokens: tokens
    });
}

/**
 * Create demo caption showcasing different text animations
 * Uses large fonts and unique typefaces for easy visibility
 */
export function createAnimationDemoCaptions(): CaptionItemImpl[] {
    const demos = [
        {
            id: 'demo-highlight-large',
            text: 'HIGHLIGHT Animation with Bold Impact',
            startAt: 0,
            duration: 8,
            position: Position.Center,
            style: { fontFamily: 'Bangers' }, // Bold, impact font
            textStyle: {
                primaryColor: '#ffffff',
                currentSpokenWordColor: '#ff6b35', // Vibrant orange
                maxFontSize: 56, // Extra large for visibility
                outline: 'stroke',
                background: 'translucent',
                textAnimation: 'highlight',
                borderRadius: 0.2,
                captionCase: 'uppercase' as const,
                hidePunctuation: false,
                containerPadding: { x: 0.15, y: 0.1 },
                boxPadding: { x: 0, y: 0 }
            }
        },
        
        {
            id: 'demo-pop-elegant',
            text: 'Elegant POP Animation with Serif Style',
            startAt: 10,
            duration: 7,
            position: Position.Center,
            style: { fontFamily: 'Playfair Display' }, // Elegant serif
            textStyle: {
                primaryColor: '#2c3e50',
                currentSpokenWordColor: '#e74c3c',
                maxFontSize: 54,
                outline: '3d-shadow',
                background: 'white',
                textAnimation: 'pop',
                borderRadius: 0.25,
                captionCase: 'standard' as const,
                hidePunctuation: false,
                containerPadding: { x: 0.2, y: 0.12 },
                boxPadding: { x: 0, y: 0 }
            }
        },
        
        {
            id: 'demo-karaoke-modern',
            text: 'Modern KARAOKE Style Progressive Reveal',
            startAt: 20,
            duration: 9,
            position: Position.Center,
            style: { fontFamily: 'Inter' }, // Modern sans-serif
            textStyle: {
                primaryColor: '#ffffff',
                currentSpokenWordColor: '#00d4ff', // Bright cyan
                maxFontSize: 50,
                outline: 'glow',
                background: 'colored',
                textAnimation: 'karaoke',
                borderRadius: 0.3,
                captionCase: 'standard' as const,
                hidePunctuation: false,
                containerPadding: { x: 0.18, y: 0.1 },
                boxPadding: { x: 0, y: 0 }
            }
        },
        
        {
            id: 'demo-boxed-playful',
            text: 'BOXED Animation Creates Fun Energy',
            startAt: 32,
            duration: 8,
            position: Position.Center,
            style: { fontFamily: 'Fredoka One' }, // Playful, rounded font
            textStyle: {
                primaryColor: '#ffffff',
                currentSpokenWordColor: '#ff4081', // Hot pink
                maxFontSize: 54,
                outline: 'none',
                background: 'none',
                textAnimation: 'boxed',
                borderRadius: 0.4,
                captionCase: 'uppercase' as const,
                hidePunctuation: false,
                containerPadding: { x: 0.1, y: 0.05 },
                boxPadding: { x: 0.12, y: 0.08 }
            }
        },
        
        {
            id: 'demo-reveal-scale-dramatic',
            text: 'DRAMATIC Reveal Scale Animation',
            startAt: 42,
            duration: 6,
            position: Position.Center,
            style: { fontFamily: 'Oswald' }, // Strong, condensed font
            textStyle: {
                primaryColor: '#f39c12',
                currentSpokenWordColor: '#e67e22',
                maxFontSize: 58,
                outline: '3d-stroke',
                background: 'none',
                textAnimation: 'reveal_scale',
                borderRadius: 0.1,
                captionCase: 'uppercase' as const,
                hidePunctuation: false,
                containerPadding: { x: 0.1, y: 0.05 },
                boxPadding: { x: 0, y: 0 }
            }
        },
        
        {
            id: 'demo-slide-up-smooth',
            text: 'SLIDE UP Animation - Smooth Vertical Motion',
            startAt: 50,
            duration: 6,
            position: Position.Center,
            style: { fontFamily: 'Lora' }, // Readable serif
            textStyle: {
                primaryColor: '#2c3e50',
                currentSpokenWordColor: '#8e44ad',
                maxFontSize: 54, // Larger for better visibility
                outline: 'shadow',
                background: 'white',
                textAnimation: 'slide_up',
                borderRadius: 0.2,
                captionCase: 'uppercase' as const, // Emphasize for demo
                hidePunctuation: false,
                containerPadding: { x: 0.15, y: 0.08 },
                boxPadding: { x: 0, y: 0 }
            }
        },

        {
            id: 'demo-slide-left-dynamic',
            text: 'SLIDE LEFT Animation - Dynamic Horizontal Entry',
            startAt: 58,
            duration: 6,
            position: Position.Center,
            style: { fontFamily: 'Roboto Condensed' }, // Clean, modern
            textStyle: {
                primaryColor: '#ffffff',
                currentSpokenWordColor: '#ff4081', // Hot pink
                maxFontSize: 52,
                outline: 'glow',
                background: 'translucent',
                textAnimation: 'slide_left',
                borderRadius: 0.15,
                captionCase: 'uppercase' as const,
                hidePunctuation: false,
                containerPadding: { x: 0.12, y: 0.06 },
                boxPadding: { x: 0, y: 0 }
            }
        },
        
        {
            id: 'demo-karaoke-box-combo',
            text: 'KARAOKE BOX Combination Effect',
            startAt: 66,
            duration: 8,
            position: Position.Center,
            style: { fontFamily: 'Titan One' }, // Bold display font
            textStyle: {
                primaryColor: '#ffffff',
                currentSpokenWordColor: '#00ff88', // Bright green
                maxFontSize: 56,
                outline: '3d-shadow',
                background: 'none',
                textAnimation: 'karaoke_box',
                borderRadius: 0.35,
                captionCase: 'uppercase' as const,
                hidePunctuation: false,
                containerPadding: { x: 0.1, y: 0.05 },
                boxPadding: { x: 0.1, y: 0.06 }
            }
        }
    ];

    return demos.map(generateTokensForDemo);
}

/**
 * Create outline effects demo showing different outline styles
 * Uses contrasting colors and large fonts for clear visibility
 */
export function createOutlineEffectsDemoCaptions(): CaptionItemImpl[] {
    const demos = [
        {
            id: 'demo-glow-effect',
            text: 'GLOWING TEXT Creates Ethereal Beauty',
            startAt: 76,
            duration: 6,
            position: Position.Center,
            style: { fontFamily: 'Comfortaa' }, // Rounded, modern
            textStyle: {
                primaryColor: '#ffffff',
                currentSpokenWordColor: '#64b5f6', // Light blue
                maxFontSize: 54,
                outline: 'glow',
                background: 'none',
                textAnimation: 'scale',
                borderRadius: 0.1,
                captionCase: 'uppercase' as const,
                hidePunctuation: false,
                containerPadding: { x: 0.1, y: 0.05 },
                boxPadding: { x: 0, y: 0 }
            }
        },
        
        {
            id: 'demo-3d-stroke-bold',
            text: 'Bold 3D STROKE Effect',
            startAt: 84,
            duration: 5,
            position: Position.Center,
            style: { fontFamily: 'Black Han Sans' }, // Bold Korean-style
            textStyle: {
                primaryColor: '#ffffff',
                currentSpokenWordColor: '#ff5722', // Deep orange
                maxFontSize: 62,
                outline: '3d-stroke',
                background: 'none',
                textAnimation: 'pop',
                borderRadius: 0.1,
                captionCase: 'uppercase' as const,
                hidePunctuation: false,
                containerPadding: { x: 0.1, y: 0.05 },
                boxPadding: { x: 0, y: 0 }
            }
        },
        
        {
            id: 'demo-3d-shadow-deep',
            text: 'Deep 3D Shadow Depth',
            startAt: 91,
            duration: 6,
            position: Position.Center,
            style: { fontFamily: 'Staatliches' }, // Condensed display
            textStyle: {
                primaryColor: '#e91e63', // Hot pink
                currentSpokenWordColor: '#ad1457', // Darker pink
                maxFontSize: 56,
                outline: '3d-shadow',
                background: 'translucent',
                textAnimation: 'reveal',
                borderRadius: 0.15,
                captionCase: 'uppercase' as const,
                hidePunctuation: false,
                containerPadding: { x: 0.12, y: 0.08 },
                boxPadding: { x: 0, y: 0 }
            }
        }
    ];

    return demos.map(generateTokensForDemo);
}

/**
 * Create font showcase demo with unique typefaces
 * Highlights the variety of Google Fonts available
 */
export function createFontShowcaseDemoCaptions(): CaptionItemImpl[] {
    const demos = [
        {
            id: 'demo-font-creepster',
            text: 'CREEPSTER Font for Horror Vibes',
            startAt: 99,
            duration: 5,
            position: Position.Center,
            style: { fontFamily: 'Creepster' }, // Spooky display font
            textStyle: {
                primaryColor: '#4caf50', // Matrix green
                currentSpokenWordColor: '#81c784',
                maxFontSize: 52,
                outline: 'glow',
                background: 'none',
                textAnimation: 'reveal_up',
                borderRadius: 0.1,
                captionCase: 'uppercase' as const,
                hidePunctuation: false,
                containerPadding: { x: 0.1, y: 0.05 },
                boxPadding: { x: 0, y: 0 }
            }
        },
        
        {
            id: 'demo-font-satisfy',
            text: 'Satisfy Script for Elegant Touch',
            startAt: 106,
            duration: 6,
            position: Position.Center,
            style: { fontFamily: 'Satisfy' }, // Handwritten script
            textStyle: {
                primaryColor: '#6a1b9a', // Deep purple
                currentSpokenWordColor: '#ab47bc', // Lighter purple
                maxFontSize: 60,
                outline: 'shadow',
                background: 'white',
                textAnimation: 'slide_left',
                borderRadius: 0.25,
                captionCase: 'standard' as const,
                hidePunctuation: false,
                containerPadding: { x: 0.18, y: 0.1 },
                boxPadding: { x: 0, y: 0 }
            }
        },
        
        {
            id: 'demo-font-rubik-wet',
            text: 'RUBIK WET PAINT Artistic Style',
            startAt: 114,
            duration: 7,
            position: Position.Center,
            style: { fontFamily: 'Rubik Wet Paint' }, // Artistic paint effect
            textStyle: {
                primaryColor: '#ff9800', // Orange
                currentSpokenWordColor: '#f57c00', // Dark orange
                maxFontSize: 54,
                outline: 'stroke',
                background: 'colored',
                textAnimation: 'karaoke_scale',
                borderRadius: 0.3,
                captionCase: 'uppercase' as const,
                hidePunctuation: false,
                containerPadding: { x: 0.16, y: 0.1 },
                boxPadding: { x: 0, y: 0 }
            }
        }
    ];

    return demos.map(generateTokensForDemo);
}

/**
 * Create comprehensive caption demo with all effects
 * Combines all the individual demos into one showcase
 */
export function createComprehensiveCaptionDemo(): CaptionItemImpl[] {
    return [
        ...createAnimationDemoCaptions(),
        ...createOutlineEffectsDemoCaptions(),
        ...createFontShowcaseDemoCaptions()
    ];
}

/**
 * Demo categories for organized showcase
 */
export const CAPTION_DEMO_CATEGORIES = {
    animations: {
        title: 'Text Animation Showcase',
        description: 'Large-scale demonstrations of 14 text animation types',
        captions: createAnimationDemoCaptions
    },
    outlines: {
        title: 'Outline Effects Showcase', 
        description: 'Bold outline effects with high-contrast colors',
        captions: createOutlineEffectsDemoCaptions
    },
    fonts: {
        title: 'Unique Font Showcase',
        description: 'Distinctive Google Fonts with artistic flair',
        captions: createFontShowcaseDemoCaptions
    },
    comprehensive: {
        title: 'Complete Caption Demo',
        description: 'All caption features in one comprehensive showcase',
        captions: createComprehensiveCaptionDemo
    }
};

/**
 * Export the main demo function for easy usage
 */
export default createComprehensiveCaptionDemo;